<template>
  <div
    class="base-textarea base-textarea__outer"
    :class="[
      className,
      {
        disabled,
        error: error && !focused,
        'error-counter': hasCounterError,
        focused,
      },
    ]"
  >
    <div class="base-textarea__wrapper">
      <slot
        :id="textareaId"
        name="label"
        :value="modelValue"
      >
        <label
          v-if="label"
          class="base-textarea__label"
          :for="textareaId"
        >
          {{ label }}
        </label>
      </slot>

      <div class="base-textarea__inner">
        <slot
          :id="textareaId"
          name="prefix"
          :value="modelValue"
        />

        <textarea
          :id="textareaId"
          ref="textareaElement"
          v-model="modelValue"
          class="base-textarea__element"
          :class="classScrollbar"
          :disabled
          :name
          :placeholder="placeholder ?? label"
          :rows
          :style="autoresize ? textareaStyle : undefined"
          v-bind="attrs"
          @focusin="onFocusIn"
          @focusout="onFocusOut"
          @input="onInput"
          @keydown="onKeyDown"
        />

        <slot
          :id
          name="suffix"
          :value="modelValue"
        />
      </div>

      <slot
        :id="textareaId"
        name="counter"
        :number-characters
        :value="modelValue"
      >
        <span
          v-if="counter"
          class="base-textarea__counter"
          :class="[{ error: hasCounterError }]"
        >
          {{ numberCharacters }}/{{ counter }}
        </span>
      </slot>
    </div>

    <div
      v-if="((error || slots.error) && !focused) || hint || slots.hint"
      class="base-textarea__messages"
    >
      <slot
        :id="textareaId"
        name="error"
        :value="modelValue"
      >
        <p
          v-if="error && !focused"
          class="base-textarea__error"
        >
          {{ error }}
        </p>
      </slot>

      <slot
        :id="textareaId"
        :hint
        name="hint"
        :value="modelValue"
      >
        <p
          v-if="hint"
          class="base-textarea__hint"
        >
          {{ hint }}
        </p>
      </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { HTMLAttributes, TextareaHTMLAttributes } from "vue";

interface Props extends /* @vue-ignore */ TextareaHTMLAttributes {
  autoresize?: boolean;
  class?: TextareaHTMLAttributes["class"];
  classScrollbar?: HTMLAttributes["class"];
  counter?: string;
  disabled?: boolean;
  error?: string;
  hint?: string;
  id?: TextareaHTMLAttributes["id"];
  label?: string;
  maxRows?: TextareaHTMLAttributes["rows"];
  name: TextareaHTMLAttributes["name"];
  placeholder?: TextareaHTMLAttributes["placeholder"];
  rows?: TextareaHTMLAttributes["rows"];
}

const props = withDefaults(defineProps<Props>(), {
  autoresize: true,
  rows: 3,
});

const emit = defineEmits<{
  submit: [];
}>();

const modelValue = defineModel<TextareaHTMLAttributes["value"]>();

defineSlots<{
  counter: [id: TextareaHTMLAttributes["id"], numberCharacters: number, value: TextareaHTMLAttributes["value"]];
  error: [id: TextareaHTMLAttributes["id"], value: TextareaHTMLAttributes["value"]];
  hint: [id: TextareaHTMLAttributes["id"], value: TextareaHTMLAttributes["value"]];
  label: [id: TextareaHTMLAttributes["id"], value: TextareaHTMLAttributes["value"]];
  prefix: [id: TextareaHTMLAttributes["id"], value: TextareaHTMLAttributes["value"]];
  suffix: [id: TextareaHTMLAttributes["id"], value: TextareaHTMLAttributes["value"]];
}>();

defineOptions({
  inheritAttrs: false,
});

const attrs = useAttrs();
const slots = useSlots();
const { autoresize, class: className, counter, id, maxRows, name, rows } = toRefs(props);
const textareaElement = ref<HTMLTextAreaElement | null>(null);
const textareaId = id.value ?? name.value;
const focused = ref(false);
const textareaHeight = ref<string>('auto');
const isOverflowed = ref(false);

const numberCharacters = computed(() => {
  return modelValue.value ? String(modelValue.value).length : 0;
});

const hasCounterError = computed(() => {
  return numberCharacters.value > Number(counter.value);
});

const textareaStyle = computed(() => ({
  height: textareaHeight.value,
  overflow: isOverflowed.value ? 'auto' : 'hidden',
}));

const onFocusIn = () => {
  focused.value = true;
};

const onFocusOut = () => {
  focused.value = false;
};

const adjustHeight = () => {
  if (!autoresize.value || !textareaElement.value) {
    return;
  }

  // Сбрасываем высоту для правильного расчета scrollHeight
  textareaHeight.value = 'auto';

  nextTick(() => {
    if (!textareaElement.value) {
      return;
    }

    const styles = window.getComputedStyle(textareaElement.value);
    const lineHeight = Number.parseInt(styles.lineHeight, 10);
    const { scrollHeight } = textareaElement.value;

    // Если указан maxRows, ограничиваем высоту
    if (maxRows.value !== undefined) {
      const maxHeight = lineHeight * Number(maxRows.value);
      if (scrollHeight > maxHeight) {
        textareaHeight.value = `${maxHeight}px`;
        isOverflowed.value = true;
        return;
      }
    }

    textareaHeight.value = `${scrollHeight}px`;
    isOverflowed.value = false;
  });
};

const onInput = () => {
  adjustHeight();
};

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === "Enter" && event.ctrlKey) {
    event.preventDefault();
    textareaElement.value?.blur();
    emit("submit");
  }

  if (event.key === "Escape") {
    textareaElement.value?.blur();
  }
};

if (autoresize.value) {
  watch(
    [modelValue, textareaElement],
    async () => {
      await nextTick(() => {
        adjustHeight();
      });
    },
    {
      immediate: true,
    },
  );
}

onMounted(() => {
  if (autoresize.value) {
    adjustHeight();
  }
});

onClickOutside(textareaElement, () => {
  if (focused.value) {
    textareaElement.value?.blur();
  }
});

defineExpose({
  textareaElement,
});
</script>

<style scoped>
.base-textarea__outer {
  display: flex;
  flex-direction: column;
}

.base-textarea__wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.base-textarea__inner {
  display: flex;
  width: 100%;
}

.base-textarea__label {
  display: flex;
}

.base-textarea__element {
  width: 100%;
  padding: 0;
  font: inherit;
  resize: none;
  border: none;
  outline: none;
  background-color: transparent;
}

.base-textarea__messages {
  display: flex;
  flex-direction: column;
}
</style>
